"use client";

import { ProfileLink } from "@/components/profile/profile-link";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

export interface FeedCardHeaderProps {
  /** User display name */
  displayName: string;
  /**
   * Username / handle for the profile link. When absent, the header renders the
   * avatar + name non-clickable and hides the `@handle` (never links to a raw
   * user id, which 404s).
   */
  handle?: string | null;
  /** Avatar image URL */
  avatarUrl?: string | null;
  /** ISO date string for datetime attribute */
  createdAt: string | Date;
  /**
   * Relative time display (e.g., "2h", "3d"). Derived from the caller's clock,
   * which the server and the hydrating browser do not share — a post can cross
   * a boundary between the two passes. The `<time>` that renders this is
   * therefore `suppressHydrationWarning`: a label one tick stale for a moment
   * beats React discarding the whole card as a #418 text mismatch. Everything
   * else about the label is pinned in `@/lib/dates/relative-time`.
   */
  timeAgo: string;
  /** Whether to hide the @handle */
  hideHandle?: boolean;
  /** Custom text in place of the @handle */
  subtitleText?: string;
  /** Whether the user is blocked */
  isBlocked?: boolean;
  /** Optional right-side content */
  rightContent?: React.ReactNode;
  /** Optional badge after timestamp (e.g., Pinned indicator) */
  extraBadge?: React.ReactNode;
  /**
   * Second line under the identity — Instagram's audio credit
   * ("♫ Song · Artist"). The avatar spans both lines.
   */
  musicLine?: React.ReactNode;
  /**
   * `x`: X's timeline frame for cards that are not `Post` — the avatar heads
   * a column and the card body (wrapped by the caller in `FEED_X_BODY`) starts
   * right under the identity line, beside the avatar, not below it.
   */
  layout?: "row" | "x";
}

/** Indent for a card body under a `layout="x"` header (children keep px-3). */
export const FEED_X_BODY = "pl-[50px]";

type FeedCardIdentityProps = Pick<
  FeedCardHeaderProps,
  | "displayName"
  | "handle"
  | "createdAt"
  | "timeAgo"
  | "hideHandle"
  | "subtitleText"
  | "isBlocked"
  | "extraBadge"
  | "musicLine"
>;

/**
 * The one header every feed card wears: avatar, then `Name · @handle · 2h` on a
 * single line (X), with an optional audio credit underneath (Instagram).
 */
export function FeedCardHeader({
  avatarUrl,
  rightContent,
  layout = "row",
  ...identity
}: FeedCardHeaderProps) {
  if (layout === "x") {
    return (
      // 52px tall (pt-3 + the 40px avatar) but pulls the body up by 14px so
      // it starts 6px under the one-line identity. The header paints above
      // that overlap, and only its avatar/identity take taps.
      <header className="pointer-events-none relative z-10 -mb-[14px] flex items-start gap-2.5 px-3 pt-3">
        <div className="pointer-events-auto">
          <FeedCardAvatar
            displayName={identity.displayName}
            handle={identity.handle}
            avatarUrl={avatarUrl}
            isBlocked={identity.isBlocked}
          />
        </div>
        <div className="pointer-events-auto flex min-w-0 flex-1">
          <FeedCardIdentity {...identity} />
        </div>
        {rightContent ? (
          <div className="pointer-events-auto">{rightContent}</div>
        ) : null}
      </header>
    );
  }
  return (
    <header className="flex items-center gap-2.5 px-3 pt-3 pb-1.5">
      <FeedCardAvatar
        displayName={identity.displayName}
        handle={identity.handle}
        avatarUrl={avatarUrl}
        isBlocked={identity.isBlocked}
      />
      <FeedCardIdentity {...identity} />
      {rightContent}
    </header>
  );
}

/** The avatar half of the header, for layouts with an avatar column (X). */
export function FeedCardAvatar({
  displayName,
  handle,
  avatarUrl,
  isBlocked = false,
}: Pick<
  FeedCardHeaderProps,
  "displayName" | "handle" | "avatarUrl" | "isBlocked"
>) {
  const name = isBlocked ? "Blocked user" : displayName;
  return (
    <ProfileLink
      handle={isBlocked ? null : handle}
      fallbackAs="div"
      className="shrink-0 transition-opacity hover:opacity-80"
    >
      <Avatar className="h-10 w-10 border border-border/50">
        <AvatarImage
          src={isBlocked ? undefined : (avatarUrl ?? undefined)}
          alt={name}
        />
        <AvatarFallback className="rounded-none text-[11px]">
          {isBlocked ? "BU" : getInitials(displayName)}
        </AvatarFallback>
      </Avatar>
    </ProfileLink>
  );
}

/** `Name · @handle · 2h` plus the optional second line. */
export function FeedCardIdentity({
  displayName,
  handle,
  createdAt,
  timeAgo,
  hideHandle = false,
  subtitleText,
  isBlocked = false,
  extraBadge,
  musicLine,
}: FeedCardIdentityProps) {
  const dateTimeString =
    typeof createdAt === "string" ? createdAt : createdAt.toISOString();
  const name = isBlocked ? "Blocked user" : displayName;
  const secondary = isBlocked
    ? "Blocked profile"
    : (subtitleText ?? (!hideHandle && handle ? `@${handle}` : null));
  const linkHandle = isBlocked ? null : handle;

  return (
    <div className="flex min-w-0 flex-1 flex-col justify-center gap-0.5">
      <div className="flex min-w-0 items-baseline gap-1 text-[14px] leading-5">
        <ProfileLink
          handle={linkHandle}
          fallbackAs="span"
          className="min-w-0 shrink truncate font-semibold text-text-primary hover:underline"
        >
          {name}
        </ProfileLink>
        {secondary ? (
          <span className="min-w-0 shrink-[2] truncate text-text-secondary">
            <span aria-hidden className="mr-1">
              ·
            </span>
            {secondary}
          </span>
        ) : null}
        <span className="flex shrink-0 items-baseline gap-1 text-text-secondary">
          <span aria-hidden>·</span>
          <time dateTime={dateTimeString} suppressHydrationWarning>
            {timeAgo}
          </time>
        </span>
        {extraBadge ? (
          <span className="flex shrink-0 items-baseline gap-1 text-[12px] text-text-secondary">
            <span aria-hidden>·</span>
            {extraBadge}
          </span>
        ) : null}
      </div>
      {musicLine ? <div className="flex min-w-0">{musicLine}</div> : null}
    </div>
  );
}

function getInitials(name: string): string {
  const cleaned = name.trim();
  if (!cleaned) return "";
  const parts = cleaned.split(" ");
  if (parts.length === 1) {
    return parts[0]?.slice(0, 2).toUpperCase() ?? "";
  }
  return `${parts[0]?.[0] ?? ""}${parts[1]?.[0] ?? ""}`.toUpperCase();
}
